import { ProductModule, Permission } from '@qualicloud/contracts';
import { Link, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, type ReactNode } from 'react';
import {
  IconAlertTriangle,
  IconBell,
  IconBuilding,
  IconCalendar,
  IconChartBar,
  IconClipboardCheck,
  IconDashboard,
  IconFiles,
  IconFolder,
  IconHistory,
  IconInbox,
  IconShieldCheck,
  IconSignature,
  IconUsers,
  cn,
  focusRing,
} from '@/design-system';
import { useAuth } from '@/providers/auth-provider';
import { linkTarget } from '@/routes/link-target';

interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: ReactNode;
  /** Permissão necessária. Ausente = visível para qualquer sessão. */
  readonly permission?: Permission;
  /**
   * Módulo de produto que governa este item. Ausente = sempre visível.
   *
   * Esconder do menu NÃO é o controle — quem bloqueia é o gateway, que devolve
   * 402 para rota de módulo não contratado. Isto existe para o usuário não ver
   * um item que só o levaria a um erro.
   *
   * A etiqueta aqui e a etiqueta da rota no gateway precisam contar a mesma
   * história. Se divergirem, a falha é benigna nos dois sentidos: item escondido
   * com rota liberada é uma tela a menos; item visível com rota bloqueada é um
   * 402 que a própria tela explica.
   */
  readonly module?: ProductModule;
}

interface NavSection {
  readonly id: string;
  readonly label: string;
  /**
   * Separa visualmente do bloco anterior. Reservado a Configurações: cadastro
   * não é trabalho do dia, e misturá-lo na mesma sequência visual das tarefas
   * faz o olho tropeçar nele todo dia à toa.
   */
  readonly detached?: boolean;
  readonly items: readonly NavItem[];
}

/*
 * ─────────────────────────────────────────────────────────────────────────────
 * A ORDEM DOS BLOCOS É O FLUXO DE TRABALHO, e o último bloco é o que se mexe uma
 * vez e não se olha mais.
 *
 * O agrupamento anterior tinha três problemas concretos:
 *
 *  1. "Qualidade" era metade tela de trabalho, metade CADASTRO: a Lista Mestra,
 *     que é a tela mais usada do produto, dividia espaço com três telas de
 *     "Tipos de…" que se abrem uma vez por trimestre. O item mais frequente
 *     ficava enterrado entre os mais raros.
 *
 *  2. Os cadastros estavam ESPALHADOS — tipos de documento em "Qualidade",
 *     usuários e organização em "Administração" — de modo que não havia um
 *     lugar para procurar quando a pergunta era "onde eu configuro isso?".
 *
 *  3. "Administração" misturava configuração com uma tela de LEITURA: a
 *     administração de documentos não configura nada, é a Lista Mestra vista
 *     por quem tem bypass de ACL. O lugar dela é junto dos documentos.
 *
 * Cada item declara a permissão MÍNIMA para ter o que fazer na tela. Abertura de
 * NC e leitura de circular ficam sem exigência porque qualquer colaborador pode
 * registrar um desvio e precisa dar ciência do que recebe; a autorização real é
 * do backend, esconder link não protege rota.
 * ─────────────────────────────────────────────────────────────────────────────
 */
const SECTIONS: readonly NavSection[] = [
  {
    id: 'work',
    label: 'Meu trabalho',
    items: [
      { to: '/', label: 'Painel', icon: <IconDashboard size={17} /> },
      {
        // Assinatura, e não prancheta: aprovar documento é ASSINAR, e a
        // prancheta ficou com Solicitações, onde ela descreve melhor a coisa.
        to: '/aprovacoes',
        label: 'Minhas aprovações',
        icon: <IconSignature size={17} />,
      },
      { to: '/notificacoes', label: 'Notificações', icon: <IconBell size={17} /> },
    ],
  },
  {
    /*
     * Documentos — o GED. A administração entra AQUI, e não num bloco de
     * administração genérico: ela não configura nada, é a mesma Lista Mestra
     * lida por quem tem bypass de ACL, e o ato fica auditado como READ.
     */
    id: 'documents',
    label: 'Documentos',
    items: [
      { module: ProductModule.GED, to: '/documentos', label: 'Lista Mestra', icon: <IconFiles size={17} /> },
      {
        // `column_left.php:545-565` do legado: o item Administração do bloco GED
        // só aparecia para quem tinha o módulo `ged_admin`. Aqui o critério é a
        // permissão funcional equivalente.
        to: '/documentos/administracao',
        module: ProductModule.GED,
        // "Administração", e não "Administração de documentos": o cabeçalho do
        // bloco já diz Documentos, e o rótulo longo estourava a largura da
        // barra e vinha truncado com reticências — o usuário lia
        // "Administração de docum…", que é pior do que a palavra sozinha.
        label: 'Administração',
        icon: <IconShieldCheck size={17} />,
        permission: Permission.DOCUMENT_READ_ALL,
      },
    ],
  },
  {
    /*
     * SGQ — Onda 2.
     *
     * A ordem é a do ciclo: NC → reunião → solicitação → circular → indicador.
     * No `column_left.php` do legado, qualidade, reunião, tarefas e circular
     * eram quatro módulos sem relação visível entre si, embora fossem etapas do
     * mesmo processo. Agrupá-los nesta ordem faz a navegação ensinar o fluxo.
     */
    id: 'sgq',
    label: 'Sistema da qualidade',
    items: [
      {
        to: '/nao-conformidades',
        module: ProductModule.NONCONFORMITY,
        label: 'Não-conformidades',
        icon: <IconAlertTriangle size={17} />,
      },
      { module: ProductModule.MEETINGS, to: '/reunioes', label: 'Reuniões', icon: <IconCalendar size={17} /> },
      {
        to: '/acoes-corretivas',
        module: ProductModule.CAPA,
        label: 'Solicitações',
        icon: <IconClipboardCheck size={17} />,
      },
      { module: ProductModule.CIRCULARS, to: '/circulares', label: 'Circulares', icon: <IconInbox size={17} /> },
      {
        to: '/indicadores-sgq',
        module: ProductModule.INDICATORS,
        label: 'Indicadores do SGQ',
        icon: <IconChartBar size={17} />,
        permission: Permission.NC_EVALUATE,
      },
    ],
  },
  {
    /*
     * A trilha tem bloco próprio, com um item só, de propósito.
     *
     * Ela não é documento nem processo do SGQ: é a evidência de TUDO que
     * acontece nos dois. Pendurá-la em qualquer um dos blocos diria que ela
     * pertence àquele assunto, e diria errado. Num sistema sob 21 CFR §11.10(e),
     * a trilha é o primeiro artefato que um inspetor pede — vale a linha de
     * cabeçalho que custa.
     */
    id: 'audit',
    label: 'Auditoria',
    items: [
      {
        to: '/auditoria',
        label: 'Trilha de auditoria',
        icon: <IconHistory size={17} />,
        permission: Permission.AUDIT_READ,
      },
    ],
  },
  {
    /*
     * CONFIGURAÇÕES — todos os cadastros, num lugar só.
     *
     * A ordem vai do mais abrangente ao mais específico: a organização contém
     * as pessoas, e as pessoas usam os catálogos. Os três catálogos repetem o
     * ícone da tela que alimentam — o calendário em "Tipos de reunião" é o mesmo
     * de "Reuniões" — porque a repetição aqui carrega significado: diz o que
     * cada cadastro configura, sem precisar de mais texto.
     */
    id: 'settings',
    label: 'Configurações',
    detached: true,
    items: [
      {
        to: '/organizacao',
        label: 'Organização',
        icon: <IconBuilding size={17} />,
        permission: Permission.ORG_MANAGE,
      },
      {
        to: '/usuarios',
        label: 'Usuários e departamentos',
        icon: <IconUsers size={17} />,
        permission: Permission.USER_MANAGE,
      },
      {
        to: '/tipos-documento',
        module: ProductModule.GED,
        label: 'Tipos de documento',
        icon: <IconFolder size={17} />,
        permission: Permission.DOCUMENT_TYPE_MANAGE,
      },
      {
        to: '/tipos-reuniao',
        module: ProductModule.MEETINGS,
        label: 'Tipos de reunião',
        icon: <IconCalendar size={17} />,
        permission: Permission.SGQ_TYPE_MANAGE,
      },
      {
        to: '/tipos-solicitacao',
        module: ProductModule.CAPA,
        label: 'Tipos de solicitação',
        icon: <IconClipboardCheck size={17} />,
        permission: Permission.SGQ_TYPE_MANAGE,
      },
    ],
  },
];

/**
 * Navegação lateral.
 *
 * ACESSIBILIDADE:
 *  · `<nav aria-label="Navegação principal">` — há mais de um `nav` na página
 *    (trilha, paginação), e sem rótulo o leitor de tela anuncia "navegação"
 *    três vezes sem distinguir;
 *  · a página atual usa `aria-current="page"`, não só destaque visual;
 *  · seções são listas dentro de `<h2>` ocultos, o que dá ao leitor de tela um
 *    índice navegável por headings;
 *  · em telas estreitas a sidebar vira gaveta: Esc fecha, o foco vai para o
 *    primeiro link ao abrir e volta ao botão ao fechar.
 *
 * Itens sem permissão são OCULTADOS aqui apenas para reduzir ruído. A
 * autorização real é do backend — esconder link não protege rota.
 */
export function Sidebar({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}) {
  const { can, hasModule } = useAuth();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    navRef.current?.querySelector<HTMLElement>('a')?.focus();
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const visibleSections = SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) =>
        (!item.permission || can(item.permission)) && (!item.module || hasModule(item.module)),
    ),
  })).filter((section) => section.items.length > 0);

  /*
   * QUAL item está ativo — uma decisão, num lugar só.
   *
   * O SINTOMA: em `/documentos/administracao`, dois links do menu apareciam
   * destacados e ambos com `aria-current="page"`. Para quem enxerga é um
   * destaque a mais; para quem usa leitor de tela é o anúncio de que a página
   * atual são duas páginas ao mesmo tempo.
   *
   * ERAM DUAS CAUSAS SOMADAS. A regra daqui casava por prefixo cru, e
   * `/documentos/administracao` começa com `/documentos`. E o `<Link>` do
   * TanStack Router marca o atributo POR CONTA PRÓPRIA — `STATIC_ACTIVE_PROPS`
   * em `link.js` —, com o casamento dele, que também é por prefixo. Passar
   * `aria-current={undefined}` não desfazia: `undefined` não apaga o que o
   * componente acrescenta.
   *
   * A correção tira a decisão do Router (`activeOptions.exact`, que o restringe
   * ao caminho idêntico) e a concentra aqui, onde há contexto para tomá-la: a
   * fronteira de SEGMENTO impede que `/documentos-antigos` case com
   * `/documentos`, e entre os que casam vence o caminho MAIS LONGO — em
   * `/documentos/administracao` ganha a administração, que é onde a pessoa
   * está; em `/documentos/42` ganha a Lista Mestra, que é a seção de onde ela
   * veio.
   */
  const activePath = visibleSections
    .flatMap((section) => section.items.map((item) => item.to))
    .filter((to) => (to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`)))
    .reduce<string | null>((maisLongo, to) => (maisLongo && maisLongo.length >= to.length ? maisLongo : to), null);

  return (
    <>
      {/* Véu da gaveta em telas estreitas. */}
      {open ? (
        <div
          aria-hidden="true"
          onClick={onClose}
          className="fixed inset-0 z-overlay bg-[rgb(15_23_42/0.45)] lg:hidden"
        />
      ) : null}

      <nav
        ref={navRef}
        aria-label="Navegação principal"
        className={cn(
          'w-60 shrink-0 overflow-y-auto border-r border-border bg-surface px-3 py-4',
          /*
           * Ancorado no desktop: `z-sticky` (1010), ABAIXO do escurecimento de
           * modal — é cromo de página, e um diálogo precisa cobri-lo. E abaixo
           * da barra superior, que também é `sticky`: sem o `lg:top-14` o menu
           * subia por cima do logotipo ao rolar.
           */
          'lg:sticky lg:top-14 lg:z-sticky lg:block lg:h-[calc(100vh-3.5rem)]',
          /*
           * GAVETA no celular: `z-modal` (1030), ACIMA do próprio véu (1020).
           *
           * O `z-sticky` morava na string BASE, valia também quando aberta, e o
           * véu ganhava dele: a gaveta abria ATRÁS do escurecimento. Pior que
           * cosmético — o véu tem `onClick={onClose}` e era ele quem recebia o
           * toque, então cada toque num item de menu fechava a gaveta em vez de
           * navegar. A navegação móvel não funcionava em nenhum viewport abaixo
           * de 1024px, inclusive janela de desktop encostada em meia tela.
           */
          open ? 'fixed inset-y-0 left-0 z-modal block h-full shadow-lg' : 'hidden',
        )}
      >
        {visibleSections.map((section) => (
          <div
            key={section.id}
            className={cn(
              'mb-5 last:mb-0',
              // Configurações desce e ganha um fio: é o bloco que se mexe uma
              // vez e não se olha mais, e não deve competir com o trabalho do dia.
              section.detached && 'mt-7 border-t border-border-subtle pt-5',
            )}
          >
            <h2
              id={`nav-section-${section.id}`}
              className="mb-1.5 px-2 text-2xs font-semibold uppercase tracking-wide text-content-subtle"
            >
              {section.label}
            </h2>
            <ul aria-labelledby={`nav-section-${section.id}`} className="flex flex-col gap-0.5">
              {section.items.map((item) => {
                const active = item.to === activePath;

                return (
                  <li key={item.to}>
                    <Link
                      {...linkTarget(item.to)}
                      // Desliga o casamento do Router; quem decide é `activePath`.
                      // Tem de ser aqui, no casamento: `link.js:198` aplica
                      // `...isActive && STATIC_ACTIVE_PROPS` por último, e nem
                      // `activeProps` vazio nem `aria-current={undefined}`
                      // desfazem o que ele acrescenta.
                      activeOptions={{ exact: true }}
                      onClick={onClose}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium transition-colors',
                        active
                          ? 'bg-brand-subtle text-brand'
                          : 'text-content-muted hover:bg-surface-sunken hover:text-content',
                        focusRing,
                      )}
                    >
                      <span aria-hidden="true" className="shrink-0">
                        {item.icon}
                      </span>
                      <span className="min-w-0 truncate">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        <p className="mt-6 border-t border-border-subtle px-2 pt-3 text-2xs text-content-subtle">
          Versão {import.meta.env.VITE_APP_VERSION ?? '0.1.0'}
          <br />
          Build {import.meta.env.VITE_BUILD_ID ?? 'local'}
        </p>
      </nav>
    </>
  );
}
